import React, { useState, useRef } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon,
  Send as SendIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { auth, db, storage } from '../firebase/config'
import { collection, addDoc, doc, getDoc } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import toast from 'react-hot-toast'

const SendQuestion = ({ teacherData, onBack }) => {
  const { user } = useAuth()
  const [description, setDescription] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedImageUrl, setSelectedImageUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Dosya türü kontrolü
      if (!file.type.startsWith('image/')) {
        toast.error('Lütfen sadece resim dosyası seçin')
        return
      }
      
      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır')
        return
      }

      setSelectedFile(file)
      
      // Önizleme için URL oluştur
      const reader = new FileReader()
      reader.onload = (e) => {
        setSelectedImageUrl(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSendQuestion = async () => {
    if (!description.trim()) {
      toast.error('Lütfen sorunuzu açıklayın')
      return
    }

    if (!selectedFile) {
      toast.error('Lütfen bir görsel seçin')
      return
    }

    setLoading(true)

    try {
      // Görseli Firebase Storage'a yükle
      const fileName = `questions/${user.uid}/${Date.now()}_${selectedFile.name}`
      const storageRef = ref(storage, fileName)
      
      await uploadBytes(storageRef, selectedFile)
      const downloadURL = await getDownloadURL(storageRef)

      // Soruyu Firestore'a kaydet
      const questionData = {
        studentId: user.uid,
        teacherId: teacherData.id,
        description: description.trim(),
        imageUrl: downloadURL,
        fileName: fileName,
        sentDate: new Date(),
        status: 'pending', // pending, answered, declined
        teacherResponse: '',
        responseDate: null
      }

      await addDoc(collection(db, 'studentQuestions'), questionData)

      toast.success('Soru başarıyla gönderildi!')
      
      // Öğretmene bildirim gönder (gelecekte implement edilecek)
      // sendNotificationToTeacher()
      
      // Ana sayfaya dön
      onBack()
      
    } catch (error) {
      console.error('Error sending question:', error)
      toast.error('Soru gönderilirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const clearSelectedFile = () => {
    setSelectedFile(null)
    setSelectedImageUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {teacherData?.name} Öğretmene Soru Gönder
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Teacher Info Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Öğretmen Bilgileri
            </Typography>
            <Typography variant="body1">
              <strong>Öğretmen:</strong> {teacherData?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sorunuzu göndermek için aşağıdaki formu doldurun
            </Typography>
          </CardContent>
        </Card>

        {/* Question Form */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Soru Açıklaması
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="Sorunuzu detaylı bir şekilde açıklayın..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
          </CardContent>
        </Card>

        {/* Image Selection */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Görsel Seçimi
            </Typography>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2 }}
            >
              Görsel Seç
            </Button>

            {selectedImageUrl && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Seçilen Görsel:
                </Typography>
                <Box
                  component="img"
                  src={selectedImageUrl}
                  alt="Seçilen görsel"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    borderRadius: 1,
                    border: '1px solid #ddd'
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={clearSelectedFile}
                  sx={{ mt: 1 }}
                >
                  Görseli Kaldır
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Önemli:</strong> Sorunuzu göndermek için hem açıklama hem de görsel gereklidir. 
            Görsel dosyası 5MB'dan küçük olmalıdır.
          </Typography>
        </Alert>

        {/* Send Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          onClick={handleSendQuestion}
          disabled={loading || !description.trim() || !selectedFile}
          sx={{
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'bold'
          }}
        >
          {loading ? 'Gönderiliyor...' : 'Soru Gönder'}
        </Button>
      </Container>
      </Box>
    </SpaceTheme>
  )
}

export default SendQuestion
